import {
  MOLECULE_LOT_FIELD_NAMES,
  MOLECULE_LOT_SCHEMA_INTERFACE_ID,
} from 'chemistry/molecule-lots/MoleculeLotConstants';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {
  GetLotPropertiesQuery_Query as GetLotPropertiesQueryResponse,
  GetLotPropertiesQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetLotPropertiesQuery';
import {GET_LOT_PROPERTIES_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  GetLotPropertiesQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

export interface LotProperties {
  parentMoleculeId: string | null;
  parentMoleculeName: string | null;
  purity: number | null;
  concentrationMolar: number | null;
  concentrationDensity: number | null;
}

function extractLotProperties(tagsJson: Array<Tag>): LotProperties {
  let parentMoleculeId: string | null = null;
  let parentMoleculeName: string | null = null;
  let purity: number | null = null;
  let concentrationMolar: number | null = null;
  let concentrationDensity: number | null = null;

  for (const tag of tagsJson) {
    if (!isSchemaTag(tag)) {
      continue;
    }
    const schemaTag = tag;
    const systemName = schemaTag.tagSchemaField.systemName;

    switch (systemName) {
      case MOLECULE_LOT_FIELD_NAMES.MOLECULE_LINK.systemName: {
        const targetFile = schemaTag.targetFiles?.[0];
        if (targetFile) {
          parentMoleculeId = targetFile.id;
          parentMoleculeName = targetFile.name ?? null;
        }
        break;
      }
      case MOLECULE_LOT_FIELD_NAMES.PURITY.systemName:
        purity = schemaTag.floatValue ?? null;
        break;
      case MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_MOLAR.systemName:
        concentrationMolar = schemaTag.floatValue ?? null;
        break;
      case MOLECULE_LOT_FIELD_NAMES.CONCENTRATION_DENSITY.systemName:
        concentrationDensity = schemaTag.floatValue ?? null;
        break;
      default:
        break;
    }
  }

  return {parentMoleculeId, parentMoleculeName, purity, concentrationMolar, concentrationDensity};
}

export async function bulkResolveGetLotPropertiesToServerResolutionResult(
  queries: Array<GetLotPropertiesQuery>
): Promise<Array<ServerResolutionResult>> {
  const lotIds = queries.map((query) => query.lotId);

  const queryResult = await doQueryWithoutCache<
    GetLotPropertiesQueryResponse,
    GetLotPropertiesQueryVariables
  >({query: GET_LOT_PROPERTIES_QUERY}, {variables: {lotIds}});

  const propertiesByLotId = new Map<string, LotProperties>();
  const nonLotEntityIds = new Set<string>();
  for (const bioentity of queryResult.bioentities) {
    if (isInaccessible(bioentity)) {
      continue;
    }
    const implementsLotInterface =
      bioentity.tagSchema?.interfaces.some((iface) => iface.id === MOLECULE_LOT_SCHEMA_INTERFACE_ID) ?? false;
    if (!implementsLotInterface) {
      nonLotEntityIds.add(bioentity.id);
      continue;
    }
    propertiesByLotId.set(bioentity.id, extractLotProperties(bioentity.tagsJson));
  }

  return queries.map((query) => {
    if (nonLotEntityIds.has(query.lotId)) {
      return makeCustomResolutionResultFromTypedValue(
        null,
        'Selected entity is not a molecule lot. Choose an entity that implements the molecule lot system category.'
      );
    }
    const properties = propertiesByLotId.get(query.lotId);
    if (properties == null) {
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve lot properties');
    }
    // Return the lot properties as a JSON typed value for downstream consumption
    return makeCustomResolutionResultFromTypedValue({
      type: BaseTypedValueType.JSON,
      jsonValue: properties,
    } as TypedValue);
  });
}

export function getLotPropertiesCustomEval(
  typedValue: TypedValue,
  _columnDefinition: unknown,
  _cellDependencies: Array<TypedValue | null>,
  _customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // If there's no lot value, nothing to resolve
  if (typedValue == null || typedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }

  const lotId = typedValue.bioentitySummary.id;

  // Trigger the resolution query so it's cached for dependent columns
  const {error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_LOT_PROPERTIES,
      lotId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );

  // Return the lot value as-is (the lot cell just displays the lot entity)
  // The resolved properties are cached and consumed by dependent columns (molecule, purity, concentration)
  return {val: typedValue, error, locked: false};
}
